import * as io from 'socket.io-client';
import {isViewMode} from "../../../../../configs/view_only";

export interface SocketIOOptions {
  constructor(options): void,
  host: string,
  port: number,
  sid: string,
  eventId: number,
  debug: boolean
}

export class SocketIO implements ServerConnectionInterface {

  private options: SocketIOOptions;
  private socket;
  public getSocket(){
    return this.socket;
  }
  private eventCallbacks: Map<string, Function[]> = new Map<string, Function[]>();
  private socketActions: string[] = [];

  private socketId: string;
  private socketProfile: Object;

  // todo: create SocketProfileInterface to describe user profiles
  private socketUsers: Map<string, Object> = new Map<string, Object>();

  protected emitBuffer: SocketIOBufferItem[] = [];
  protected emitBufferTimer = null;
  protected emitBufferDelay = 200;
  protected emitBufferMaxWait = 500;

  constructor(options: SocketIOOptions) {
    this.options = options;
  }

  logError(subject, error) {
    if (!this.options.debug) {
      return;
    }
    console.error(subject, error);
    // if (typeof Rollbar != 'undefined') {
    //   Rollbar.error(subject, error);
    // }
  }

  connect() {
    this.socket = io(this.options.host + ':' + this.options.port);
    this.initializeListeners();
  };

  initializeListeners() {
    this.socket.on('connect', () => {
      console.log("Socket connected!");
      this.dispatchEvent('connect');
      this.socket.emit('initializeClient', this.options.sid, this.options.eventId, window['location'].host)
    });

    this.socket.on('connect_error', (error) => {
      this.logError('Socket.IO connect_error', error);
      this.dispatchEvent('disconnect', [error]);
    });
    this.socket.on('connect_timeout', (error) => {
      this.logError('Socket.IO connect_timeout', error);
      this.dispatchEvent('disconnect', [error]);
    });
    this.socket.on('error', (error) => {
      this.logError('Socket.IO error', error);
      this.dispatchEvent('error', [error]);
    });
    this.socket.on('reconnect_failed', (error) => {
      this.logError('Socket.IO reconnect_failed', error);
      this.dispatchEvent('disconnect', [error]);
    });

    this.socket.on('selfId', (id) => {
      console.log('selfId', id);
      this.socketId = id;
    });

    this.socket.on('selfProfile', (profile) => {
      console.log('selfProfile', profile);
      this.socketProfile = profile;
    });

    this.socket.on('addUser', (users) => {
      console.log('addUser', users);
      for (const [key, value] of Object.entries(users)) {
        this.socketUsers.set(key, value);
        // this.dispatchEvent('addUser', [value]);
      }
    });

    this.socket.on('removeUser', (sid) => {
      console.log('removeUser', sid);
      let profile = this.socketUsers.get(sid);
      this.socketUsers.delete(sid);
      // this.dispatchEvent('removeUser', [sid, profile]);
    });

    this.socketActions.forEach((event) => { // initialize cached actions, see onAction()
      let self = this;
      this.socket.on(event, function () { self.dispatchEvent(event, arguments); });
    })
    // socket.on('reconnect_error', function (error) { logError('Socket.IO reconnect_error', error); });
    // socket.on('reconnect_failed', function (error) { logError('Socket.IO reconnect_failed', error); });
  }

  on(event: string, callback: Function) {
    if (isViewMode){
      return;
    }
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, []);
    }
    this.eventCallbacks.get(event).push(callback);
  }

  onAction(event: string, callback: Function) {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, []);
      this.socketActions.push(event);
      if (this.socket) { // if socket is already initialized
        let self = this;
        this.socket.on(event, function () { self.dispatchEvent(event, arguments); });
      }
    }
    this.eventCallbacks.get(event).push(callback);
  }

  protected addToEmitBuffer(item: SocketIOBufferItem) {
    clearTimeout(this.emitBufferTimer);
    this.emitBuffer.push(item);
    if (Date.now() - this.emitBuffer[0].createdAt.getTime() >= this.emitBufferMaxWait) {
      this._sendEmitBuffer();
    } else {
      this.emitBufferTimer = setTimeout(this._sendEmitBuffer.bind(this), this.emitBufferDelay);
    }
  }

  _sendEmitBuffer() {
    const buffer = [...this.emitBuffer];
    this.emitBuffer.length = 0;
    const silent = !buffer.some(item => !item.silent);
    if (!silent) {
      this.beforeRequest();
    }
    const data = buffer.map(item => {
      return {
        event: item.event,
        data: item.data
      };
    });
    this.socket.emit('batch', data, (results, errors) => {
      if (!silent) {
        this.afterRequest();
      }
      for (let i = 0; i < buffer.length; i++) {
        if (buffer[i].callback) {
          buffer[i].callback.apply(null, [results[i], errors[i]]);
        }
        buffer[i].promiseCallback.apply(null, [results[i], errors[i]]);
      }
      buffer.length = 0;
    });
  }

  emit(event, data, callback, silent) {
    if (isViewMode){
      return;
    }
    let self = this;
    if (location.search.indexOf('debug_socketio=1') >= 0) {
      console.log('=> SOCKETIO:', event, data);
    }
    return new Promise((resolve, reject) => {
      this.addToEmitBuffer(SocketIOBufferItem.create(
        event,
        data,
        callback,
        silent,
        resolve
      ));
    });
  }

  beforeRequest() {
    this.dispatchEvent('__beforeRequest');
  }

  afterRequest() {
    this.dispatchEvent('__afterRequest');
  }

  dispatchEvent(event: string, data?: any) {
    if (this.eventCallbacks.has(event)) {
      this.eventCallbacks.get(event).forEach((cb) => cb.apply(null, data));
    }
  }

  getUsers() {
    return this.socketUsers;
  }

}

class SocketIOBufferItem {
  public event;
  public data;
  public callback;
  public silent;
  public createdAt: Date;
  public promiseCallback: Function;

  static create(event, data, callback, silent, promiseCallback) {
    const i = new SocketIOBufferItem();
    i.event = event;
    i.data = data;
    i.callback = callback;
    i.silent = silent;
    i.createdAt = new Date();
    i.promiseCallback = promiseCallback;
    return i;
  }
}
